import React, { useEffect, useRef, useState } from 'react';

import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { getCookieValue, setRawCookie } from 'Utilities';
import { GENERIC_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsGenericEvents';
import { FLAG } from 'Utilities/analytics/optimizely/constants';
import { OPTIMIZELY_OPT_OUT_COOKIE } from 'Utilities/analytics/optimizely/optimizely';

import { useBinaryExperiment } from 'Containers/app/experiments/useBinaryExperiment';

import {
  CONSENT_CHANGED_EVENT,
  CONSENT_COOKIE_EXPIRY_DAYS,
  CONSENT_COOKIE_NAME,
  ESSENTIALS_ONLY_CONSENT,
} from '../CookieBanner/cookieConsent.constants';
import { applyConsent } from '../CookieBanner/cookieConsent.helpers';
import type { ConsentObject, ConsentValue } from '../CookieBanner/cookieConsent.types';

import { CookiePreferences } from './CookiePreferences';

const CONSENT_VALUES = new Set<ConsentValue>(['granted', 'denied']);

const isConsentValue = (value: unknown): value is ConsentValue =>
  CONSENT_VALUES.has(value as ConsentValue);

const validateConsent = (parsed: unknown): ConsentObject => {
  if (!parsed || typeof parsed !== 'object') {
    return ESSENTIALS_ONLY_CONSENT;
  }

  const source = parsed as Record<string, unknown>;

  return (Object.keys(ESSENTIALS_ONLY_CONSENT) as (keyof ConsentObject)[]).reduce(
    (acc, key) => {
      const value = source[key];
      acc[key] = isConsentValue(value) ? value : ESSENTIALS_ONLY_CONSENT[key];
      return acc;
    },
    {} as ConsentObject,
  );
};

const getStoredConsent = (): ConsentObject => {
  const raw = getCookieValue(CONSENT_COOKIE_NAME);
  if (!raw) {
    return ESSENTIALS_ONLY_CONSENT;
  }

  try {
    return validateConsent(JSON.parse(raw));
  } catch {
    return ESSENTIALS_ONLY_CONSENT;
  }
};

export const CookiePreferencesCentre = () => {
  const showCookiePreferences = useBinaryExperiment(FLAG.COOKIE_CONSENT_BANNER, {
    triggerExperiment: false,
  });

  const [mounted, setMounted] = useState(false);
  const isUpdatingRef = useRef(false);
  const [consent, setConsent] = useState<ConsentObject>(getStoredConsent);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (showCookiePreferences) {
      setConsent(getStoredConsent());
    }
  }, [showCookiePreferences]);

  useEffect(() => {
    if (!showCookiePreferences) {
      return undefined;
    }

    const handleConsentChanged = () => {
      setConsent(getStoredConsent());
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);

    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    };
  }, [showCookiePreferences]);

  if (!showCookiePreferences) {
    return null;
  }

  if (!mounted) {
    return (
      <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '275px' }}>
        <LoadingIndicator size="large" />
      </div>
    );
  }

  const updateConsent = (updatedConsent: ConsentObject) => {
    if (isUpdatingRef.current) {
      return;
    }
    isUpdatingRef.current = true;

    const persisted = applyConsent(updatedConsent);
    setConsent(updatedConsent);

    if (persisted) {
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      isUpdatingRef.current = false;
    }
  };

  const handleMarketingToggle = () => {
    const isCurrentlyGranted = consent.ad_storage === 'granted';
    const newValue = isCurrentlyGranted ? 'denied' : 'granted';

    GENERIC_ANALYTICS_EVENTS.TOGGLE_CLICK_EVENT({
      category: 'cookie_preferences',
      name: 'marketing',
      value: !isCurrentlyGranted,
    });

    updateConsent({
      ...consent,
      ad_personalization: newValue,
      ad_storage: newValue,
      ad_user_data: newValue,
      personalization_storage: newValue,
    });
  };

  const handleAnalyticsToggle = () => {
    const isCurrentlyGranted = consent.analytics_storage === 'granted';
    const newValue = isCurrentlyGranted ? 'denied' : 'granted';

    GENERIC_ANALYTICS_EVENTS.TOGGLE_CLICK_EVENT({
      category: 'cookie_preferences',
      name: 'analytics',
      value: !isCurrentlyGranted,
    });

    if (isCurrentlyGranted) {
      setRawCookie(OPTIMIZELY_OPT_OUT_COOKIE, 'true', CONSENT_COOKIE_EXPIRY_DAYS);
    } else {
      setRawCookie(OPTIMIZELY_OPT_OUT_COOKIE, '', -1);
    }

    updateConsent({
      ...consent,
      analytics_storage: newValue,
    });
  };

  const marketingEnabled = consent.ad_storage === 'granted';
  const analyticsEnabled = consent.analytics_storage === 'granted';

  return (
    <CookiePreferences
      analyticsEnabled={analyticsEnabled}
      marketingEnabled={marketingEnabled}
      onAnalyticsToggle={handleAnalyticsToggle}
      onMarketingToggle={handleMarketingToggle}
    />
  );
};
